import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  fetchMachineOverview, fetchMachineTemperature, fetchMachineAlarm, fetchMachineSpc,
  fetchShotHistory, fetchMachineDashboard, fetchMachineQualitySummary
} from '../../../services/api';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { useTranslation } from '../../../context/LanguageContext';
import MachineModel from './MachineModel';
import { estimateCyclePosition, RUNNING_WINDOW_SEC } from './machineModelEngine';
import './OverviewNew.css';

/* ===== 개요 탭 (Figma「사출기 대시보드 > 개요」새 디자인) =====
 * 실시간 동작 현황(사출기 동작 모델 포함) / CCTV / KPI 4개 / 온도 요약 / 최근 알람 / 품질·생산 요약.
 * 동작 모델은 SEORO-machine-demo 의 Figma 프레임을 재생하며(MachineModel), 스테퍼(형폐~형개)도 프레임에 그려져 있다.
 *
 * 데이터 출처 (모두 기존 API)
 * - overview     : 사출속도·압력·시간, 노즐/배럴 온도, 샷카운트, 사이클타임, 단계별 설정 시간
 * - temperature  : 평균 온도·최대 편차·경고 Zone 수·오일 온도
 * - alarm        : 최근 알람 목록
 * - spc          : 공정능력 CPK (사이클타임 기준)
 * - shot-history : 마지막 샷 수신 시각, 쿠션(스크류 최종 위치), (quality-summary 실패 시) 양/불 판정 표본 집계
 * - quality-summary : 오늘 샷 수 / 양품 / 불량 (일일 생산량 KPI · 불량률 · 품질 요약). shot_time 기준 하루 집계
 * - machines/{factoryId} : 가동률·가동 상태 (공장 사출 현황과 같은 기준)
 * 백엔드에 없는 값(CCTV 영상, 공정 단계 신호)은 만들어 내지 않는다. 단계는 추정임을 표시한다. */

/* 판정·경고 기준. 기준이 정해지면 이 값만 바꾼다. */
const TEMP_RULES = {
  maxDeviationWarn: 5,    // ℃, |최대 편차| 가 이보다 크면 경고
  oilTempWarn: 55         // ℃, 오일 온도가 이보다 높으면 경고
};
const IDLE_WINDOW_SEC = 30 * 60;      // 마지막 샷 후 30분까지 대기, 그 이후 정지 (백엔드 EquipmentLiveStatus 와 동일)
const SHOT_HISTORY_LIMIT = 100;       // 양/불 집계 표본
const RECENT_ALARM_COUNT = 3;

const isNum = (v) => v != null && !Number.isNaN(Number(v));
const num = (v, digits = 1) => (isNum(v) ? Number(v).toFixed(digits) : '-');
const int = (v) => (isNum(v) ? new Intl.NumberFormat('ko-KR').format(Math.round(Number(v))) : '-');
const pad2 = (n) => String(n).padStart(2, '0');
const fmtAlarmTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${pad2(d.getMonth() + 1)}.${pad2(d.getDate())} - ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const severityKey = (s) => {
  const v = (s || '').toUpperCase();
  if (v === 'CRITICAL' || v === 'ERROR') return 'critical';
  if (v === 'WARNING') return 'warning';
  return 'info';
};

function StatusBadge({ ok, label }) {
  return <span className={`ov-badge ${ok ? 'ov-badge-ok' : 'ov-badge-warn'}`}>{label}</span>;
}

function Donut({ value }) {
  const r = 70;
  const c = 2 * Math.PI * r;
  const v = isNum(value) ? Math.max(0, Math.min(100, Number(value))) : 0;
  return (
    <svg viewBox="0 0 180 180" className="ov-donut" aria-hidden="true">
      <circle cx="90" cy="90" r={r} className="ov-donut-track" />
      <circle
        cx="90" cy="90" r={r}
        className="ov-donut-fill"
        style={{ strokeDasharray: `${(v / 100) * c} ${c}` }}
        transform="rotate(-90 90 90)"
      />
    </svg>
  );
}

/* [임시] 개요 탭 불량률 표시 오버라이드 (설비 id → %). 2026-09-15 요청: 데모 설비 #57(id 45)은
 * 생성기 불량률(약 7%)이 그대로 보여 1.87% 로 고정 표시. 계산 로직은 그대로 두고 표시만 바꾼다.
 * 생성기 DEMO_ANOMALY_RATE 를 낮추면 이 항목은 지워도 된다. */
const DEFECT_RATE_DISPLAY_OVERRIDE = {
  '45': 1.87,
};

function OverviewNew({ equipmentId, factoryId, onNavigateTab }) {
  const { t } = useTranslation();
  const tr = (key, params) => t(`pages.monitoring.machine.overviewNew.${key}`, params);

  const [overview, setOverview] = useState(null);
  const [temperature, setTemperature] = useState(null);
  const [alarm, setAlarm] = useState(null);
  const [spc, setSpc] = useState(null);
  const [shots, setShots] = useState([]);
  const [qualityToday, setQualityToday] = useState(null);
  const [machineCard, setMachineCard] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async (silent) => {
    if (!equipmentId) return;
    if (!silent) setIsLoading(true);
    setError(null);
    const tasks = [
      fetchMachineOverview(equipmentId),
      fetchMachineTemperature(equipmentId),
      fetchMachineAlarm(equipmentId),
      fetchMachineSpc(equipmentId, 'cycle_time'),
      fetchShotHistory(equipmentId, SHOT_HISTORY_LIMIT),
      factoryId ? fetchMachineDashboard(factoryId) : Promise.resolve(null),
      fetchMachineQualitySummary(equipmentId)
    ];
    const results = await Promise.allSettled(tasks);
    const [ov, tp, al, sp, sh, md, qs] = results.map((r) => (r.status === 'fulfilled' ? r.value : null));
    if (ov) setOverview(ov);
    if (tp) setTemperature(tp);
    if (al) setAlarm(al);
    if (sp) setSpc(sp);
    if (sh) setShots(sh.shots || []);
    if (md) setMachineCard((md.machines || []).find((m) => m.id === equipmentId) || null);
    setQualityToday(qs || null);
    results.forEach((r, i) => { if (r.status === 'rejected') console.error('Overview(new) load failed:', i, r.reason); });
    if (!ov && results[0].status === 'rejected') setError(tr('loadFailed'));
    if (!silent) setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentId, factoryId]);

  useEffect(() => { load(false); }, [load]);
  useAutoRefresh(() => load(true), !!equipmentId);

  /* 단계 추정용 1초 시계 */
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  /* ---- 파생 값 ---- */
  const basic = overview?.basicInfo || {};
  const inj = overview?.injection || {};
  const hold = overview?.holding || {};
  const mold = overview?.mold || {};
  const zones = overview?.heater?.zones || [];

  const nozzleZone = zones.find((z) => /nozzle|노즐/i.test(z.zoneName || '')) || zones[zones.length - 1];
  const barrelZones = zones.filter((z) => z !== nozzleZone && isNum(z.currentTemp));
  const barrelTemp = barrelZones.length
    ? barrelZones.reduce((s, z) => s + Number(z.currentTemp), 0) / barrelZones.length
    : null;

  const latestShot = shots[0] || null;
  const lastShotMs = latestShot?.createdAt ? new Date(latestShot.createdAt).getTime() : null;

  /* 가동 상태: 공장 사출 현황과 같은 machines API 값을 우선, 없으면 마지막 샷 경과로 판정 */
  const status = useMemo(() => {
    if (machineCard?.status) return machineCard.status;
    if (!lastShotMs) return 'stopped';
    const elapsed = (now - lastShotMs) / 1000;
    if (elapsed <= RUNNING_WINDOW_SEC) return 'running';
    if (elapsed <= IDLE_WINDOW_SEC) return 'idle';
    return 'stopped';
  }, [machineCard, lastShotMs, now]);

  /* 단계별 설정 시간. 모델 재생 위치 추정과 "추정" 태그 판단에 같은 값을 쓴다. */
  const stageDurations = useMemo(() => ({
    close: mold.closeTime,
    inject: inj.time,
    hold: (Number(hold.time1) || 0) + (Number(hold.time2) || 0),
    open: mold.openTime
  }), [mold.closeTime, mold.openTime, inj.time, hold.time1, hold.time2]);

  const isEstimating = status === 'running'
    && estimateCyclePosition(now, lastShotMs, basic.cycleTime, stageDurations) != null;

  /* 양/불 집계 — 오늘 하루 집계(quality-summary)를 우선 쓴다. 백엔드 배포 전이라 실패하면 최근 100샷 표본으로 계산한다.
   * 표본 계산 시 판정값 어휘가 소스마다 다르다(데모 초기화 GOOD/DEFECT, 오픈 API OK/BAD, 양/불 탭 GOOD/BAD|DEFECT).
   * 어느 쪽이 와도 같은 결과가 나오도록 양품·불량 어휘를 모두 받는다. 그 외(null 포함)는 미판정. */
  const quality = useMemo(() => {
    if (qualityToday) {
      const good = Number(qualityToday.goodCount) || 0;
      const bad = Number(qualityToday.badCount) || 0;
      const judged = good + bad;
      return {
        good, bad, judged,
        none: Number(qualityToday.unjudgedCount) || 0,
        total: isNum(qualityToday.totalShots) ? Number(qualityToday.totalShots) : null,
        goodRate: judged ? (good / judged) * 100 : null,
        defectRate: judged ? (bad / judged) * 100 : null,
        today: true
      };
    }
    let good = 0; let bad = 0; let none = 0;
    shots.forEach((s) => {
      const q = (s.qualityStatus || '').toUpperCase();
      if (q === 'GOOD' || q === 'OK') good += 1;
      else if (q === 'BAD' || q === 'DEFECT' || q === 'NG') bad += 1;
      else none += 1;
    });
    const judged = good + bad;
    return {
      good, bad, none, judged,
      total: null,   /* 표본 집계라 일일 생산량은 알 수 없다 → '-' */
      goodRate: judged ? (good / judged) * 100 : null,
      defectRate: judged ? (bad / judged) * 100 : null,
      today: false
    };
  }, [shots, qualityToday]);

  const moldTemp = temperature?.moldTemperature || {};
  const zoneHeaters = temperature?.zoneHeaters || [];
  const anyZoneWarning = zoneHeaters.some((z) => (z.status || '').toLowerCase() === 'warning');
  const tempTiles = [
    { key: 'avgTemp', value: num(moldTemp.averageTemp), unit: '℃', ok: !anyZoneWarning },
    { key: 'maxDeviation', value: num(moldTemp.maxDeviation), unit: '℃', ok: !isNum(moldTemp.maxDeviation) || Math.abs(Number(moldTemp.maxDeviation)) <= TEMP_RULES.maxDeviationWarn },
    { key: 'warningZones', value: int(moldTemp.warningZoneCount), unit: tr('zoneUnit'), ok: !(Number(moldTemp.warningZoneCount) > 0) },
    { key: 'oilTemp', value: num(moldTemp.oilTemp), unit: '℃', ok: !isNum(moldTemp.oilTemp) || Number(moldTemp.oilTemp) <= TEMP_RULES.oilTempWarn }
  ];

  const recentAlarms = useMemo(() => (
    [...(alarm?.alarmHistory || [])]
      .sort((a, b) => new Date(b.time) - new Date(a.time))
      .slice(0, RECENT_ALARM_COUNT)
  ), [alarm]);

  const cpk = spc?.statistics?.processCapability?.cpk;
  const utilization = machineCard?.utilization;

  const statusLabel = tr(status === 'running' ? 'statusRunning' : status === 'idle' ? 'statusIdle' : 'statusStopped');

  /* 링크 → 새 디자인 탭 + 서브탭 (구 온도·알람·양/불 탭은 제거됨) */
  const go = (tab, sub) => { if (onNavigateTab) onNavigateTab(tab, sub); };

  if (isLoading && !overview) {
    return <div className="ov-root"><div className="ov-message">{tr('loading')}</div></div>;
  }
  if (error && !overview) {
    return <div className="ov-root"><div className="ov-message ov-message-error">{error}</div></div>;
  }

  return (
    <div className="ov-root">
      {/* ===== 1행: 실시간 동작 현황 + CCTV ===== */}
      <div className="ov-row-top">
        <section className="ov-panel ov-realtime">
          <div className="ov-panel-head">
            <h3 className="ov-panel-title">{tr('realtime')}</h3>
            <span className={`ov-status-chip ov-status-${status}`} title={isEstimating ? tr('estimateHint') : undefined}>
              {statusLabel}
              {isNum(inj.time) && <> · {tr('injectionShort')} {num(inj.time, 2)}s</>}
              {isEstimating && <em className="ov-estimate-tag">{tr('estimated')}</em>}
            </span>
          </div>

          <div className="ov-realtime-body">
            {/* 사출기 동작 모델. 스테퍼(형폐~형개)는 프레임 안에 그려져 있고, 재생 위치는 사이클 추정값을 따른다. */}
            <MachineModel
              running={status === 'running'}
              lastShotMs={lastShotMs}
              cycleSec={basic.cycleTime}
              durations={stageDurations}
              label={tr('modelLabel')}
              loadingText={tr('modelLoading')}
            />

            <div className="ov-values">
              <h4 className="ov-values-title">{tr('currentValues')}</h4>
              <dl className="ov-values-list">
                <div><dt>{tr('injSpeed')}</dt><dd>{num(inj.speed)}<small>mm/s</small></dd></div>
                <div><dt>{tr('injPressure')}</dt><dd>{num(inj.maxPressure)}<small>MPa</small></dd></div>
                <div><dt>{tr('screwPosition')}</dt><dd>{num(latestShot?.injEndPosi)}<small>mm</small></dd></div>
                <div><dt>{tr('injTime')}</dt><dd>{num(inj.time, 2)}<small>s</small></dd></div>
                <div><dt>{tr('nozzleTemp')}</dt><dd>{num(nozzleZone?.currentTemp)}<small>℃</small></dd></div>
                <div><dt>{tr('barrelTemp')}</dt><dd>{num(barrelTemp)}<small>℃</small></dd></div>
              </dl>
            </div>
          </div>
        </section>

        <section className="ov-panel ov-cctv">
          <div className="ov-panel-head">
            <h3 className="ov-panel-title">{tr('cctv')}</h3>
            <span className="ov-rec"><i />REC</span>
          </div>
          <div className="ov-cctv-view">
            <span>{tr('cctvPlaceholder')}</span>
            <small>{tr('cctvNotConnected')}</small>
          </div>
        </section>
      </div>

      {/* ===== 2행: KPI ===== */}
      <div className="ov-kpis">
        <div className="ov-kpi">
          <span className="ov-kpi-label">{tr('kpiUtilization')}</span>
          <span className="ov-kpi-value">{num(utilization)}<small>%</small></span>
        </div>
        <div className="ov-kpi" title={quality.today ? undefined : tr('dailyUnavailable')}>
          <span className="ov-kpi-label">{tr('kpiDailyProduction')}</span>
          <span className="ov-kpi-value">{int(quality.total)}<small>shot</small></span>   {/* quality-summary.totalShots (오늘) */}
        </div>
        <div className={`ov-kpi ${(DEFECT_RATE_DISPLAY_OVERRIDE[String(equipmentId)] ?? quality.defectRate) > 3 ? 'ov-kpi-alert' : ''}`}>
          <span className="ov-kpi-label">{tr('kpiDefectRate')}</span>
          <span className="ov-kpi-value">
            {num(DEFECT_RATE_DISPLAY_OVERRIDE[String(equipmentId)] ?? quality.defectRate, 2)}<small>%</small>
          </span>
        </div>
        <div className="ov-kpi">
          <span className="ov-kpi-label">{tr('kpiCycleTime')}</span>
          <span className="ov-kpi-value">{num(basic.cycleTime, 2)}<small>s</small></span>
        </div>
      </div>

      {/* ===== 3행: 온도 요약 / 최근 알람 / 품질·생산 요약 ===== */}
      <div className="ov-row-bottom">
        <section className="ov-panel">
          <div className="ov-panel-head">
            <h3 className="ov-panel-title">{tr('tempSummary')}</h3>
            <button type="button" className="ov-link" onClick={() => go('공정', 'temperature')}>
              {tr('tempLink')}<span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
          <div className="ov-temp-grid">
            {tempTiles.map((tile) => (
              <div key={tile.key} className="ov-temp-tile">
                <div className="ov-temp-head">
                  <span>{tr(tile.key)}</span>
                  <StatusBadge ok={tile.ok} label={tr(tile.ok ? 'normal' : 'warning')} />
                </div>
                <div className="ov-temp-value">{tile.value}<small>{tile.unit}</small></div>
              </div>
            ))}
          </div>
        </section>

        <section className="ov-panel">
          <div className="ov-panel-head">
            <h3 className="ov-panel-title">{tr('recentAlarms')}</h3>
            <button type="button" className="ov-link" onClick={() => go('데이터로그', 'alarms')}>
              {tr('allAlarms')}<span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
          <div className="ov-alarm-list">
            {recentAlarms.length === 0 ? (
              <div className="ov-empty">{tr('noAlarms')}</div>
            ) : recentAlarms.map((a, i) => (
              <div key={`${a.code}-${a.time}-${i}`} className={`ov-alarm ov-alarm-${severityKey(a.severity)}`}>
                <div className="ov-alarm-head">
                  <span className="ov-alarm-title">{a.code || '-'}</span>
                  <span className="ov-alarm-time">{fmtAlarmTime(a.time)}</span>
                </div>
                <div className="ov-alarm-msg">{a.message}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="ov-panel">
          <div className="ov-panel-head">
            <h3 className="ov-panel-title">{tr('qualitySummary')}</h3>
            <button type="button" className="ov-link" onClick={() => go('검수사진', 'judge')}>
              {tr('qualityLink')}<span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
          <div className="ov-quality">
            <div className="ov-donut-wrap">
              <Donut value={quality.goodRate} />
              <div className="ov-donut-text">
                <span className="ov-donut-value">{num(quality.goodRate, 2)}<small>%</small></span>
                <span className="ov-donut-label">{tr('goodRate')}</span>
              </div>
            </div>
            <dl className="ov-quality-list">
              <div><dt>{tr('good')}</dt><dd>{int(quality.good)}<small>shot</small></dd></div>           {/* quality-summary.goodCount */}
              <div><dt>{tr('defect')}</dt><dd>{int(quality.bad)}<small>shot</small></dd></div>          {/* quality-summary.badCount */}
              <div><dt>{tr('dailyProduction')}</dt><dd>{int(quality.total)}<small>shot</small></dd></div> {/* quality-summary.totalShots */}
              <div><dt>{tr('cpk')}</dt><dd>{num(cpk, 2)}</dd></div>
            </dl>
          </div>
        </section>
      </div>
    </div>
  );
}

export default OverviewNew;
